/**
 * Klausuren und Lernplan (Roadmap F-22, Stufe 3).
 *
 * Die Klausur ist ein Termin mit `subjectId` und `klausur` - sie steht damit
 * im Kalender und bei den Deadlines. Lernplan-Eintraege sind Aufgaben mit
 * `examId` und `plannedDate`, erscheinen also im Tagesplan und im Kalender.
 */
import { db } from '@/db/schema';
import { createEvent, deleteEvent } from '@/db/repositories/events';
import { createTask } from '@/db/repositories/tasks';
import { inDenPapierkorb, papierkorbTitel } from '@/db/repositories/papierkorb';
import { klausurDatum, lerntage } from '@/domain/school/exams';
import { nowInstant, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { CalendarEvent, ID, KlausurThema, LocalDate, Task } from '@/domain/types';

export interface NeueKlausur {
  subjectId: ID;
  titel: string;
  datum: LocalDate;
  /** 'HH:mm' */
  von: string;
  bis: string;
  raum?: string;
}

export async function createExam(k: NeueKlausur): Promise<CalendarEvent | null> {
  const [fach, settings] = await Promise.all([db.subjects.get(k.subjectId), db.schoolSettings.get('schule')]);
  if (!fach || !k.datum || !(k.von < k.bis)) return null;
  return createEvent({
    title: k.titel.trim() || `Klausur ${fach.name}`,
    allDay: false,
    start: `${k.datum}T${k.von}`,
    end: `${k.datum}T${k.bis}`,
    tz: 'Europe/Berlin',
    areaId: settings?.areaId ?? null,
    subjectId: fach.id,
    klausur: { themen: [] },
    ...(k.raum?.trim() ? { location: k.raum.trim() } : {}),
  });
}

async function themenAendern(eventId: ID, fn: (t: KlausurThema[]) => KlausurThema[]): Promise<void> {
  await db.events.where('id').equals(eventId).modify((e) => {
    if (!e.klausur) return;
    e.klausur = { themen: fn(e.klausur.themen) };
    e.updatedAt = nowInstant();
  });
}

export async function addThema(eventId: ID, text: string): Promise<void> {
  const t = text.trim();
  if (!t) return;
  await themenAendern(eventId, (l) => [...l, { id: newId(), text: t, done: false }]);
}

export async function toggleThema(eventId: ID, themaId: ID): Promise<void> {
  await themenAendern(eventId, (l) => l.map((t) => (t.id === themaId ? { ...t, done: !t.done } : t)));
}

export async function removeThema(eventId: ID, themaId: ID): Promise<void> {
  const thema = (await db.events.get(eventId))?.klausur?.themen.find((t) => t.id === themaId);
  await inDenPapierkorb({ art: 'entfernt', tabelle: 'events', zeileIds: [eventId], titel: papierkorbTitel(thema?.text) },
    () => themenAendern(eventId, (l) => l.filter((t) => t.id !== themaId)));
}

/** Ein Lernplan-Eintrag: eine Aufgabe fuer einen Tag vor der Klausur. */
export async function addLernEintrag(eventId: ID, datum: LocalDate, text: string): Promise<Task | null> {
  const e = await db.events.get(eventId);
  const titel = text.trim();
  if (!e?.klausur || !titel || !datum || datum >= klausurDatum(e)) return null;
  return createTask({
    title: titel,
    subjectId: e.subjectId,
    examId: e.id,
    areaId: e.areaId,
    plannedDate: datum,
    status: 'naechste',
  });
}

/**
 * Taegliche Lerneintraege von heute bis zum Vortag - nur fuer Tage, die
 * noch keinen haben. Manuell ausgeloest; kein automatischer Plan.
 */
export async function lerntageAnlegen(eventId: ID): Promise<number> {
  const e = await db.events.get(eventId);
  if (!e?.klausur) return 0;
  const vorhanden = new Set(
    (await db.tasks.filter((t) => t.examId === eventId).toArray()).map((t) => t.plannedDate),
  );
  let n = 0;
  for (const tag of lerntage(today(), klausurDatum(e))) {
    if (vorhanden.has(tag)) continue;
    await addLernEintrag(eventId, tag, `Lernen: ${e.title}`);
    n += 1;
  }
  return n;
}

/** Loescht die Klausur samt ihrem Lernplan - als EIN Papierkorb-Eintrag (F-45). */
export async function deleteExam(eventId: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'events', zeileIds: [eventId] }, () => db.transaction('rw', db.events, db.tasks, db.terminBestaetigungen, async () => {
    await db.tasks.filter((t) => t.examId === eventId).delete();
    await deleteEvent(eventId);
  }));
}
